import type React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { Alert, LinearProgress, Typography } from '@mui/material';
import { setupI18n } from '../i18n';
import { EmptyContent } from '@imminently/react/ui/core/empty-content';
import { useAuth } from 'react-oidc-context';
import { getSessionItemWithExpiry, setSessionItemWithExpiry } from '@/util/MiscUtils';

interface I18nContextType {
  isInitialized: boolean;
  error: Error | null;
}

const I18nContext = createContext<I18nContextType>({
  isInitialized: false,
  error: null
});

export const useI18n = () => useContext(I18nContext);

interface I18nProviderProps {
  children: React.ReactNode;
}

export const I18nProvider: React.FC<I18nProviderProps> = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const auth = useAuth();
  // TODO
  //const roles = useAppUserRole();
  //const { currentTenancyId } = useTenancies();
  const roles = ['admin']; // Just so we can get latest i18n every time
  const currentTenancyId = 'trier';
  useEffect(() => {
    if (!auth.isAuthenticated || !auth.user) {
      return;
    }
    const lang = roles.includes('admin') ? {} : getSessionItemWithExpiry("trier.lang");
    const initializeI18n = async () => {
      try {
        const { success, resources, language } = await setupI18n({
          tenancy: currentTenancyId,
          token: auth.user?.id_token,
          providedResources: lang || {},
          defaultLanguage: "en",
          skipBackend: false,
        });
        if (resources) setSessionItemWithExpiry("trier.lang", {
          [language]: resources
        }, 1000 * 60 * 60 * 24);
        setIsInitialized(success);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to initialize i18n'));
      }
    };

    initializeI18n();
  }, [currentTenancyId, auth.isAuthenticated]);

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        width: '100vw'
      }}>
        <EmptyContent
          image='/trier-full-horizontal-dark.svg'
          width='400px'
        >

          <Typography mb={4}>
            <Alert severity='error'>Failed to load translations</Alert>
            <Typography>{error?.message}</Typography>
          </Typography>
        </EmptyContent>
      </div>
    )
  }

  // Don't block UI if user isn't authenticated - let them sign in first
  if (!isInitialized && auth.isAuthenticated) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        width: '100vw'
      }}>
        <EmptyContent
          image='/trier-full-horizontal-dark.svg'
          width='400px'
        >

          <Typography mb={4}>
            Loading translations...
            <LinearProgress />

          </Typography>
        </EmptyContent>
      </div>
    );
  }

  return (
    <I18nContext.Provider value={{ isInitialized, error }}>
      {children}
    </I18nContext.Provider>
  );
};
